import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp, useFetch } from '../store/app';
import { Fade, FilterSelect, BulkBar, BulkSelect, BulkAction, CheckBox } from './common';
import { Icon } from '../components/Icon';
import { Button, Avatar, Tag } from '../components/primitives';
import { st, prio } from '../lib/status';
import { initials } from '../lib/avatar';
import { money } from '../lib/format';
import { api } from '../lib/api';
import { can } from '../lib/rbac';
import { useSelection } from '../lib/useSelection';
import type { Client } from '../../shared/types';

interface ProjAssignee { name: string; kind: string; role?: string; fee?: number }
interface ProjCard {
  id: number;
  name: string;
  type: string;
  client_name: string;
  status: string;
  priority: string;
  due_date: string;
  tags: string[];
  assignees: ProjAssignee[];
  bid_amount?: number | null;
  report_type?: string;
  month_bucket?: string;
  audit_start?: string;
  audit_end?: string;
  facility_label?: string;
  facility_state?: string;
  facility_type?: string;
  stage_progress?: { done: number; total: number; current: string } | null;
}

const COLUMNS: { key: string; title: string; accent: string }[] = [
  { key: 'planning', title: 'Planning', accent: 'var(--info)' },
  { key: 'active', title: 'In progress', accent: 'var(--accent)' },
  { key: 'at-risk', title: 'Review', accent: 'var(--warning)' },
  { key: 'done', title: 'Done', accent: 'var(--success)' },
];

const AV_KIND: Record<string, string> = { a: 'var(--accent-bg)|var(--accent)', i: 'var(--info-bg)|var(--info)', s: 'var(--success-bg)|var(--success)', w: 'var(--warning-bg)|var(--warning)', n: 'var(--surface-3)|var(--text-2)' };
const avColors = (k: string) => (AV_KIND[k] || AV_KIND.n).split('|');

const PRIORITIES = ['High', 'Medium', 'Low'];

export function Projects() {
  const { openDrawer, refresh, user } = useApp();
  const nav = useNavigate();
  const { data } = useFetch<ProjCard[]>('/projects');
  const { data: clientsData } = useFetch<Client[]>('/clients');
  const all = data || [];
  const [type, setType] = useState('all');
  const [client, setClient] = useState('all');
  const [owner, setOwner] = useState('all');

  const types = Array.from(new Set(all.map((p) => p.type).filter(Boolean))).sort();
  const clientNames = Array.from(new Set(all.map((p) => p.client_name).filter((n) => n && n !== '—'))).sort();
  const owners = Array.from(new Set(all.flatMap((p) => p.assignees.map((a) => a.name)))).sort();
  const projects = all.filter(
    (p) =>
      (type === 'all' || p.type === type) &&
      (client === 'all' || p.client_name === client) &&
      (owner === 'all' || p.assignees.some((a) => a.name === owner)),
  );

  const canBulk = !!user && (can(user.role, 'update', 'projects') || can(user.role, 'delete', 'projects'));
  const sel = useSelection(projects.map((p) => p.id));

  const bulkUpdate = async (patch: Record<string, string | number>) => {
    await api.post('/projects/bulk', { action: 'update', ids: sel.ids, patch });
    sel.clear();
    refresh();
  };
  const bulkDelete = async () => {
    if (!confirm(`Delete ${sel.count} project${sel.count === 1 ? '' : 's'}? This can't be undone.`)) return;
    await api.post('/projects/bulk', { action: 'delete', ids: sel.ids });
    sel.clear();
    refresh();
  };

  // PREA (and future non-kanban types) open a detail page; General keeps the drawer.
  const openProject = (p: ProjCard) => (p.type === 'PREA Audit' ? nav(`/projects/${p.id}`) : openDrawer({ type: 'project', mode: 'edit', id: p.id }));
  const scheduleView = type === 'PREA Audit';

  // Kanban drag-to-reorder status. Targeted bulk update so no other field is touched.
  const canDrag = !!user && can(user.role, 'update', 'projects');
  const [dragId, setDragId] = useState<number | null>(null);
  const [overCol, setOverCol] = useState<string | null>(null);
  const moveTo = async (pid: number, status: string) => {
    const cur = all.find((p) => p.id === pid);
    if (!cur || cur.status === status) return;
    await api.post('/projects/bulk', { action: 'update', ids: [pid], patch: { status } });
    refresh();
  };

  return (
    <Fade>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <FilterSelect icon="category" value={type} onChange={setType} options={[{ label: 'All types', value: 'all' }, ...types.map((t) => ({ label: t, value: t }))]} />
        <FilterSelect icon="apartment" value={client} onChange={setClient} options={[{ label: 'All clients', value: 'all' }, ...clientNames.map((n) => ({ label: n, value: n }))]} />
        <FilterSelect icon="group" value={owner} onChange={setOwner} options={[{ label: 'Any owner', value: 'all' }, ...owners.map((n) => ({ label: n, value: n }))]} />
        <div style={{ flex: 1 }} />
        <Button icon="add" onClick={() => openDrawer({ type: 'project', mode: 'create', context: scheduleView ? { type: 'PREA Audit' } : undefined })}>New project</Button>
      </div>

      {scheduleView ? (
        <ScheduleTable rows={projects} sel={sel} canBulk={canBulk} onOpen={openProject} />
      ) : (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, alignItems: 'start' }}>
        {COLUMNS.map((col) => {
          const cards = projects.filter((p) => p.status === col.key);
          const isOver = overCol === col.key;
          return (
            <div
              key={col.key}
              onDragOver={canDrag ? (e) => { e.preventDefault(); if (overCol !== col.key) setOverCol(col.key); } : undefined}
              onDragLeave={canDrag ? (e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOverCol((c) => (c === col.key ? null : c)); } : undefined}
              onDrop={canDrag ? () => { if (dragId != null) moveTo(dragId, col.key); setDragId(null); setOverCol(null); } : undefined}
              style={{ background: isOver ? 'var(--accent-bg)' : 'var(--surface-2)', border: `1px solid ${isOver ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 12, padding: 10, minHeight: 160, transition: 'background .12s, border-color .12s' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 6px 11px' }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: col.accent }} />
                <span style={{ fontWeight: 600, fontSize: 13 }}>{col.title}</span>
                <span className="cc-mono" style={{ fontSize: 11.5, color: 'var(--text-3)', fontWeight: 600 }}>{cards.length}</span>
                <div style={{ flex: 1 }} />
                <span onClick={() => openDrawer({ type: 'project', mode: 'create', context: { status: col.key } })} className="cc-clickable"><Icon name="add" size={18} color="var(--text-3)" /></span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                {cards.map((c) => {
                  const pr = prio(c.priority);
                  return (
                    <div
                      key={c.id}
                      onClick={() => openProject(c)}
                      draggable={canDrag}
                      onDragStart={canDrag ? (e) => { setDragId(c.id); e.dataTransfer.effectAllowed = 'move'; } : undefined}
                      onDragEnd={canDrag ? () => { setDragId(null); setOverCol(null); } : undefined}
                      className="cc-hover-cardb cc-clickable"
                      style={{ background: sel.has(c.id) ? 'var(--accent-bg)' : 'var(--surface)', border: `1px solid ${sel.has(c.id) ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 10, padding: 12, boxShadow: 'var(--shadow-sm)', opacity: dragId === c.id ? 0.5 : 1 }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 7 }}>
                        {canBulk && <CheckBox checked={sel.has(c.id)} onChange={() => sel.toggle(c.id)} />}
                        <div style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.3, flex: 1, minWidth: 0 }}>{c.name}</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                        <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{c.client_name}</span>
                        {c.type && c.type !== 'General' && <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.02em', color: 'var(--accent)', background: 'var(--accent-bg)', padding: '1px 6px', borderRadius: 4 }}>{c.type}</span>}
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 11 }}>
                        {c.tags.map((t) => <Tag key={t}>{t}</Tag>)}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {c.assignees.map((a, i) => {
                            const [bg, fg] = avColors(a.kind);
                            return <div key={i} style={{ marginRight: -7 }}><Avatar initials={initials(a.name)} bg={bg} fg={fg} size={24} ring /></div>;
                          })}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                          <span style={{ fontSize: 10.5, fontWeight: 600, padding: '2px 7px', borderRadius: 5, background: pr.bg, color: pr.fg }}>{pr.label}</span>
                          <span style={{ fontSize: 11, color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 3 }}><Icon name="event" size={14} />{c.due_date}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      )}

      {canBulk && (
        <BulkBar count={sel.count} onClear={sel.clear}>
          {can(user!.role, 'update', 'projects') && (
            <>
              <BulkSelect label="Set stage" icon="view_kanban" onPick={(v) => bulkUpdate({ status: v })} options={COLUMNS.map((col) => ({ label: col.title, value: col.key }))} />
              <BulkSelect label="Set priority" icon="flag" onPick={(v) => bulkUpdate({ priority: v })} options={PRIORITIES.map((p) => ({ label: p, value: p }))} />
              {(clientsData || []).length > 0 && <BulkSelect label="Set client" icon="apartment" onPick={(v) => bulkUpdate({ client_id: Number(v) })} options={(clientsData || []).map((c) => ({ label: c.name, value: String(c.id) }))} />}
            </>
          )}
          {can(user!.role, 'delete', 'projects') && <BulkAction label="Delete" icon="delete" danger onClick={bulkDelete} />}
        </BulkBar>
      )}
    </Fade>
  );
}

const SCHED_COLS = '28px 1.8fr 0.5fr 1.2fr 1.3fr 1.2fr 1.1fr 0.9fr 0.9fr 0.9fr';

function ScheduleTable({ rows, sel, canBulk, onOpen }: { rows: ProjCard[]; sel: ReturnType<typeof useSelection>; canBulk: boolean; onOpen: (p: ProjCard) => void }) {
  const feeOf = (p: ProjCard) => p.assignees.reduce((s, a) => s + (a.fee || 0), 0);
  const totBid = rows.reduce((s, p) => s + (p.bid_amount || 0), 0);
  const totFee = rows.reduce((s, p) => s + feeOf(p), 0);
  const th = { fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase' as const, letterSpacing: '.04em' };
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, boxShadow: 'var(--shadow-sm)', overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: 900 }}>
          <div style={{ display: 'grid', gridTemplateColumns: SCHED_COLS, gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--border)', alignItems: 'center', ...th }}>
            <div>{canBulk ? <CheckBox checked={sel.allChecked} indeterminate={sel.someChecked} onChange={sel.toggleAll} /> : null}</div>
            <div>Facility</div><div>State</div><div>Client</div><div>Auditor(s)</div><div>Stage</div><div>Dates</div>
            <div style={{ textAlign: 'right' }}>Bid</div><div style={{ textAlign: 'right' }}>Fee</div><div style={{ textAlign: 'right' }}>Profit</div>
          </div>
          {rows.map((p) => {
            const fee = feeOf(p);
            const dates = [p.audit_start, p.audit_end].filter(Boolean).join(' – ') || p.due_date || '—';
            const sp = p.stage_progress;
            return (
              <div key={p.id} onClick={() => onOpen(p)} className="cc-hover-s2 cc-clickable" style={{ display: 'grid', gridTemplateColumns: SCHED_COLS, gap: 12, padding: '11px 16px', borderBottom: '1px solid var(--border)', alignItems: 'center', background: sel.has(p.id) ? 'var(--accent-bg)' : undefined }}>
                <div>{canBulk ? <CheckBox checked={sel.has(p.id)} onChange={() => sel.toggle(p.id)} /> : null}</div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.facility_label || p.name}</div>
                  {p.facility_type && <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{p.facility_type}</div>}
                </div>
                <div className="cc-mono" style={{ fontSize: 12.5, color: 'var(--text-2)' }}>{p.facility_state || '—'}</div>
                <div style={{ fontSize: 12.5, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.client_name}</div>
                <div style={{ fontSize: 12.5, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.assignees.map((a) => a.name).join(', ') || '—'}</div>
                <div>{sp ? <StagePill sp={sp} /> : '—'}</div>
                <div className="cc-mono" style={{ fontSize: 11.5, color: 'var(--text-2)' }}>{dates}</div>
                <div className="cc-mono" style={{ fontSize: 12.5, textAlign: 'right' }}>{money(p.bid_amount)}</div>
                <div className="cc-mono" style={{ fontSize: 12.5, textAlign: 'right', color: 'var(--text-2)' }}>{money(fee)}</div>
                <div className="cc-mono" style={{ fontSize: 12.5, textAlign: 'right', fontWeight: 600, color: (p.bid_amount || 0) - fee < 0 ? 'var(--danger)' : 'var(--success)' }}>{money((p.bid_amount || 0) - fee)}</div>
              </div>
            );
          })}
          {rows.length === 0 && <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>No PREA audits yet. Create one with “New project”.</div>}
          {rows.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: SCHED_COLS, gap: 12, padding: '12px 16px', alignItems: 'center', background: 'var(--surface-2)', fontWeight: 700 }}>
              <div /><div style={{ fontSize: 12.5 }}>{rows.length} audits</div><div /><div /><div /><div /><div style={{ textAlign: 'right', ...th }}>Totals</div>
              <div className="cc-mono" style={{ fontSize: 12.5, textAlign: 'right' }}>{money(totBid)}</div>
              <div className="cc-mono" style={{ fontSize: 12.5, textAlign: 'right' }}>{money(totFee)}</div>
              <div className="cc-mono" style={{ fontSize: 12.5, textAlign: 'right', color: 'var(--success)' }}>{money(totBid - totFee)}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StagePill({ sp }: { sp: { done: number; total: number; current: string } }) {
  const complete = sp.done === sp.total;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, fontWeight: 600, color: complete ? 'var(--success)' : 'var(--text-2)' }}>
      <span style={{ width: 30, height: 5, borderRadius: 3, background: 'var(--surface-3)', overflow: 'hidden', display: 'inline-block' }}>
        <span style={{ display: 'block', height: '100%', width: `${Math.round((sp.done / sp.total) * 100)}%`, background: complete ? 'var(--success)' : 'var(--accent)' }} />
      </span>
      {sp.current}
    </span>
  );
}
